/**
 * CommentThreadBlock – Renders a list of review comments (unresolved + optional resolved) with resolve action.
 * Used inline under a form section (ProgressNoteForm) or in QRDocumentWithComments.
 */
import {
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import type { DocumentComment } from '../types';

export interface CommentThreadBlockProps {
  /** Stable key for expand/collapse state (e.g. itemPath). */
  itemPath: string;
  comments: DocumentComment[];
  onResolveComment?: (commentId: string) => void;
  /** When true, show Resolve button on unresolved comments. */
  canResolve?: boolean;
}

export function CommentThreadBlock({
  comments,
  onResolveComment,
  canResolve = false,
}: CommentThreadBlockProps): JSX.Element {
  const [resolvedExpanded, setResolvedExpanded] = useState(false);

  const byCreated = (a: DocumentComment, b: DocumentComment) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  const unresolved = comments.filter((c) => !c.resolvedAt).sort(byCreated);
  const resolved = comments.filter((c) => c.resolvedAt).sort(byCreated);
  const hasResolved = resolved.length > 0;

  const commentRow = (c: DocumentComment, isResolved: boolean) => (
    <Box
      key={c.id}
      p="xs"
      style={{
        backgroundColor: isResolved
          ? 'var(--mantine-color-gray-0)'
          : 'var(--mantine-color-blue-0)',
        borderRadius: 8,
        borderLeft: isResolved
          ? undefined
          : '3px solid var(--mantine-color-blue-5)',
      }}
    >
      <Group justify="space-between" mb={4} wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Text size="xs" fw={600}>
            {c.authorName}
          </Text>
          <Text size="xs" c="gray.6">
            {new Date(c.createdAt).toLocaleString()}
          </Text>
          {isResolved && (
            <Badge size="xs" color="green" variant="light">
              Resolved
            </Badge>
          )}
        </Group>
        {!isResolved && canResolve && onResolveComment && (
          <Button
            size="xs"
            variant="subtle"
            color="green"
            onClick={() => onResolveComment(c.id)}
          >
            Resolve
          </Button>
        )}
      </Group>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {c.text}
      </Text>
    </Box>
  );

  return (
    <Box mt="xs">
      <Stack gap="xs">
        {unresolved.map((c) => commentRow(c, false))}
      </Stack>
      {hasResolved && (
        <>
          <UnstyledButton
            onClick={() => setResolvedExpanded((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: 6,
              backgroundColor: 'var(--mantine-color-gray-0)',
              width: '100%',
              textAlign: 'left',
              marginTop: unresolved.length > 0 ? 8 : 0,
            }}
          >
            {resolvedExpanded ? (
              <IconChevronDown size={14} color="var(--mantine-color-gray-6)" />
            ) : (
              <IconChevronRight size={14} color="var(--mantine-color-gray-6)" />
            )}
            <Text size="xs" c="gray.7" fw={500}>
              Resolved · {resolved.length} comment
              {resolved.length !== 1 ? 's' : ''}
            </Text>
          </UnstyledButton>
          <Collapse in={resolvedExpanded}>
            <Stack gap="xs" mt="xs">
              {resolved.map((c) => commentRow(c, true))}
            </Stack>
          </Collapse>
        </>
      )}
    </Box>
  );
}
